/**
 * Hotels API
 *
 * Endpoints for hotel management, room availability,
 * statistics, and image handling.
 */

import { apiClient } from './client'
import type { Hotel, ApiResponse } from './types'
import type { CommunicationPreferences, CommunicationPreferencesPatch } from './communication-preferences.types'

/**
 * Hotel search and management parameters
 */
/** #589: per-hotel image stored in hotels.metadata.images */
export interface HotelImage {
  id: string
  url: string
  title?: string
  // #1870: 'enrichment' = imported on the admin Media Enrichment page
  // (hotel_media); 'upload' = uploaded on Hotel Details (metadata.images).
  source?: 'enrichment' | 'upload'
}

interface HotelSearchParams {
  page?: number
  limit?: number
  search?: string
  city?: string
  country?: string
  status?: string
  sortBy?: string
  order?: 'asc' | 'desc'
  // #417: batch fetch by ids — avoids N+1 fan-out on admin-facing pages
  ids?: string[]
  // #2223: include test hotels (admin-only; the server ignores it for others).
  includeTest?: boolean
}

/**
 * Hotel management endpoints
 */
export const hotelsApi = {
  // ==================== Hotel Search & Retrieval ====================

  /**
   * Search hotels with filters
   */
  getHotels: (params?: HotelSearchParams): Promise<ApiResponse<Hotel[]>> => {
    const query: Record<string, string> = {}
    if (params?.search) query.search = params.search
    if (params?.city) query.city = params.city
    if (params?.country) query.country = params.country
    if (params?.status) query.status = params.status
    if (params?.sortBy) query.sortBy = params.sortBy
    if (params?.order) query.order = params.order
    if (params?.page) query.page = String(params.page)
    if (params?.includeTest) query.includeTest = 'true'
    if (params?.ids && params.ids.length > 0) {
      query.hotel_ids = params.ids.join(',')
      query.limit = String(params.limit ?? Math.max(params.ids.length, 50))
    } else if (params?.limit) {
      query.limit = String(params.limit)
    }
    return apiClient.get('/hotels', { params: query })
  },

  /**
   * #591: Fetch ALL hotels by walking every page of /hotels/search.
   * The backend caps `limit` at 100 and orders by createdAt DESC, so a
   * single page-1 call silently drops the oldest hotels once the platform
   * exceeds 100 (e.g. "Ein Harod", position 118 of 119, vanished from every
   * picker). Use this for any dropdown/picker that needs the full list.
   */
  getAllHotels: async (params: HotelSearchParams = {}): Promise<Hotel[]> => {
    // #599: request status=all so admin pickers list hotels of every status;
    // the backend only honours it for admin tokens, so non-admin callers
    // still get the active-only default.
    const baseParams = { status: 'all', ...params }
    const firstPage = await hotelsApi.getHotels({ ...baseParams, limit: 100, page: 1 })
    const all = [...(firstPage.data || [])]
    // #591 follow-up: the API returns camelCase `totalPages`; the original
    // snake_case read made this loop a silent no-op (only page 1 fetched).
    const pagination = (firstPage as any).pagination
    const totalPages = pagination?.totalPages || pagination?.total_pages || 1
    if (totalPages > 1) {
      const remaining = []
      for (let page = 2; page <= totalPages; page++) {
        remaining.push(hotelsApi.getHotels({ ...baseParams, limit: 100, page }))
      }
      const results = await Promise.all(remaining)
      results.forEach(r => { if (r.data) all.push(...r.data) })
    }
    return all
  },

  /**
   * Get a specific hotel by ID
   */
  getHotel: (id: string): Promise<ApiResponse<Hotel>> =>
    apiClient.get(`/hotels/${id}`),

  // ==================== Hotel Management ====================

  /**
   * Create a new hotel
   */
  createHotel: (data: Partial<Hotel>): Promise<ApiResponse<Hotel>> =>
    apiClient.post('/hotels', data),

  /**
   * Update hotel information
   */
  updateHotel: (id: string, data: Partial<Hotel>): Promise<ApiResponse<Hotel>> =>
    apiClient.put(`/hotels/${id}`, data),

  /**
   * #2087: save only the hotel's go-live status (admin-only key on
   * PUT /hotels/:id), apart from the full hotel form. A change to Paused ends
   * the hotel's active affiliations: the server answers 409
   * pause_confirmation_required (details.affiliationsToEnd) until the call
   * carries confirmPause.
   */
  updateGoLiveStatus: (
    id: string,
    onboardingStatus: string,
    opts?: { confirmPause?: boolean; reason?: string }
  ): Promise<ApiResponse<Hotel>> =>
    apiClient.put(`/hotels/${id}`, {
      onboardingStatus,
      ...(opts?.confirmPause ? { confirmPause: true } : {}),
      // #2287 part 7: the written reason a move to Ready / Live with affs needs.
      ...(opts?.reason ? { goLiveReason: opts.reason } : {}),
    }),

  /** #1331 Part 1b-B — admin-only WhatsApp consent + preferred channel. */
  getCommunicationPreferences: (id: string): Promise<ApiResponse<CommunicationPreferences>> =>
    apiClient.get(`/hotels/${id}/communication-preferences`),
  updateCommunicationPreferences: (id: string, patch: CommunicationPreferencesPatch): Promise<ApiResponse<CommunicationPreferences>> =>
    apiClient.put(`/hotels/${id}/communication-preferences`, patch),

  /**
   * #589: Upload a hotel image. Stored in hotels.metadata.images; returns the
   * new image plus the full updated list so the page can re-render.
   */
  uploadHotelImage: (id: string, file: File, title?: string): Promise<ApiResponse<{ image: HotelImage; images: HotelImage[]; gallery?: HotelImage[] }>> => {
    const formData = new FormData()
    formData.append('image', file)
    if (title) formData.append('title', title)
    return apiClient.post(`/hotels/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // #594: file uploads can't finish within the global 10s timeout on
      // slower uplinks — allow up to 2 minutes for the multipart stream.
      timeout: 120000,
    })
  },

  /**
   * #589: Remove a hotel image by id; returns the full updated list.
   */
  deleteHotelImage: (id: string, imageId: string): Promise<ApiResponse<{ images: HotelImage[]; gallery?: HotelImage[] }>> =>
    apiClient.delete(`/hotels/${id}/images/${imageId}`),

  // ==================== Statistics ====================

  // ==================== Ownership Management ====================

  getOwners: (hotelId: string): Promise<ApiResponse<{
    primaryOwnerId: string | null;
    primaryOwner: { id: string; email: string | null; firstName: string | null; lastName: string | null } | null;
    coOwners: Array<{ user_id: string; role: string; granted_by: string | null; granted_at: string; email?: string | null; firstName?: string | null; lastName?: string | null }>;
  }>> =>
    apiClient.get(`/hotels/${hotelId}/owners`),

  assignPrimaryOwner: (hotelId: string, userId: string): Promise<ApiResponse<void>> =>
    apiClient.put(`/hotels/${hotelId}/owner`, { userId }),

  addOwner: (hotelId: string, userId: string): Promise<ApiResponse<void>> =>
    apiClient.post(`/hotels/${hotelId}/owners`, { userId }),

  removeOwner: (hotelId: string, userId: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/hotels/${hotelId}/owners/${userId}`),

}

export default hotelsApi
